import { defineStore } from "pinia";

import { ref, computed } from "vue";

import UserService from "@/services/client";
import RBACService from "@/services/rbac";

import { useAppStore } from "./app";

import storage from "@/services/storage";

const rbacEnabledStorageKey = "rbac_enabled";

export const useUserStore = defineStore("user", () => {
  const storedUser = storage.getJSONItem("user", {});
  const storedToken =
    (import.meta.env?.VITE_PERSIST_SESSION ?? "false") === "true"
      ? storage.getItem("token", "")
      : "";
  // const storedPartner = storage.getJSONItem('partner', {})
  const appStore = useAppStore();

  const user = ref(storedUser);
  const roles = ref(storage.getJSONItem("roles", []));
  const permissions = ref(storage.getJSONItem("permissions", []));
  const partner = ref({});
  const origin_partner = ref({});
  const partnerInfo = ref([]);
  const disabledPermissions = ref([]);
  const rbacEnabled = ref(storage.getJSONItem(rbacEnabledStorageKey, false) === true);
  const rbacStateLoaded = ref(false);

  const token = ref(storedToken);

  const mustLogin = ref(false);
  const maintenance = ref(false);

  const us = new UserService();
  const rbacService = new RBACService();

  const isLogged = computed(() => token.value !== "" || mustLogin.value);

  function normalizePermissionNames(list) {
    return Array.isArray(list)
      ? [...new Set(list.filter(Boolean).map((permission) => String(permission).trim()).filter(Boolean))]
      : [];
  }

  function syncDisabledPermissions(permissionList) {
    disabledPermissions.value = normalizePermissionNames(
      (Array.isArray(permissionList) ? permissionList : [])
        .filter((permission) => permission?.enabled === false)
        .map((permission) => permission?.name)
    );

    return disabledPermissions.value;
  }

  function setRBACEnabled(value) {
    rbacEnabled.value = value === true;
    storage.setJSONItem(rbacEnabledStorageKey, rbacEnabled.value);
    return rbacEnabled.value;
  }

  async function refreshPermissionAvailability() {
    if (!token.value) {
      disabledPermissions.value = [];
      rbacStateLoaded.value = true;
      return [];
    }

    try {
      const response = await rbacService.getPermissions(token.value);
      const permissionsList = Array.isArray(response?.data) ? response.data : [];
      syncDisabledPermissions(permissionsList);
    } catch (err) {
      disabledPermissions.value = [];
    }

    rbacStateLoaded.value = true;

    return disabledPermissions.value;
  }

  async function refreshRBACState() {
    if (!token.value) {
      disabledPermissions.value = [];
      setRBACEnabled(false);
      rbacStateLoaded.value = true;
      return { enabled: false, disabledPermissions: [] };
    }

    try {
      const [settingsResponse, permissionsResponse] = await Promise.all([
        rbacService.getSettings(token.value),
        rbacService.getPermissions(token.value),
      ]);

      const enabled = settingsResponse?.data?.enabled === true;
      const permissionsList = Array.isArray(permissionsResponse?.data) ? permissionsResponse.data : [];

      setRBACEnabled(enabled);
      syncDisabledPermissions(permissionsList);
      rbacStateLoaded.value = true;

      return { enabled, disabledPermissions: disabledPermissions.value };
    } catch (err) {
      setRBACEnabled(false);
      disabledPermissions.value = [];
      rbacStateLoaded.value = true;
      return { enabled: false, disabledPermissions: [] };
    }
  }

  async function login(loginData) {
    const response = await us.login(loginData);
    token.value = response.data.data.access_token;
    user.value = response.data.data.user;
    roles.value = response.data.data.roles ?? [];
    permissions.value = response.data.data.permissions ?? [];
    storage.setItem("token", response.data.data.access_token);
    storage.setJSONItem("user", response.data.data.user);
    storage.setJSONItem("roles", roles.value);
    storage.setJSONItem("permissions", permissions.value);
    await refreshRBACState();
    appStore.setVersion(response.data.data.version);
  }

  async function ssoGoogleLogin(idToken) {
    const response = await us.ssoGoogle(idToken, "client");
    token.value = response.data.data.access_token;
    user.value = response.data.data.user;
    roles.value = response.data.data.roles ?? [];
    permissions.value = response.data.data.permissions ?? [];
    storage.setItem("token", response.data.data.access_token);
    storage.setJSONItem("user", response.data.data.user);
    storage.setJSONItem("roles", roles.value);
    storage.setJSONItem("permissions", permissions.value);
    await refreshRBACState();
    appStore.setVersion(response.data.data.version);
  }

  async function ssoMicrosoftLogin(idToken) {
    const response = await us.ssoMicrosoft(idToken, "client");
    token.value = response.data.data.access_token;
    user.value = response.data.data.user;
    roles.value = response.data.data.roles ?? [];
    permissions.value = response.data.data.permissions ?? [];
    storage.setItem("token", response.data.data.access_token);
    storage.setJSONItem("user", response.data.data.user);
    storage.setJSONItem("roles", roles.value);
    storage.setJSONItem("permissions", permissions.value);
    await refreshRBACState();
    appStore.setVersion(response.data.data.version);
  }

  function hasPermission(permission) {
    if (user.value?.master === true) {
      return true;
    }

    if (!rbacEnabled.value) {
      return true;
    }

    if (disabledPermissions.value.includes(permission)) {
      return true;
    }

    return permissions.value.includes(permission);
  }

  async function changePassword(loginData) {
    const data = await us.changePassword(loginData);
    return true;
  }

  async function recoverPassword(loginData) {
    const data = await us.recoverPassword(loginData);
    return true;
  }
  //ZSLW-66
  async function getPartner() {
    const data = await us.getPartner(token.value);
    origin_partner.value = data?.data?.[0] ?? {};
    partner.value = data?.data?.[0] ?? {};
  }

  async function getPartnerInfo() {
    const data = await us.getPartnerInfo(token.value);
    partnerInfo.value = data?.data ?? [];
  }
  //ZSLW-66
  async function savePartner(vivawallet) {
    const partnerData = {
      ...partner.value,
      partner_info: vivawallet,
    };

    const response = await us.savePartner(token.value, partnerData);
    return response;
  }

  async function logout(loginData) {
    mustLogin.value = false;
    maintenance.value = false;
    await reset();
  }

  async function reset() {
    token.value = "";
    user.value = {};
    roles.value = [];
    permissions.value = [];
    disabledPermissions.value = [];
    rbacEnabled.value = false;
    rbacStateLoaded.value = false;
    storage.removeItem("token");
    storage.removeItem("user");
    storage.removeItem("partner");
    storage.removeItem("roles");
    storage.removeItem("permissions");
    storage.removeItem(rbacEnabledStorageKey);
  }

  async function setMustLogin(reload) {
    mustLogin.value = true;
    reset();
    // if (reload) window.location.reload()
  }

  function endsWith(str, suffix) {
    return str.indexOf(suffix, str.length - suffix.length) !== -1;
  }

  async function setMaintenance(reload) {
    maintenance.value = true;
    reset();
    if (!endsWith(window.location.href, "/maintenance"))
      window.location = "/maintenance";
  }

  async function getServerVersion() {
    const data = await us.getServerVersion();
    return data;
  }

  async function ping() {
    const data = await us.ping();
    return data;
  }
  async function enableWebhook(vivapayload) {
    const data = await us.createVivawalletWebhook(token.value, vivapayload);
    return data;
  }

  async function getVivaWalletWebhookStatus() {
    const data = await us.getVivaWalletWebhookStatus(
      token.value,
      partner.value.codigo
    );

    return data?.data ?? {};
  }

  return {
    user,
    roles,
    permissions,
    disabledPermissions,
    rbacEnabled,
    rbacStateLoaded,
    hasPermission,
    refreshPermissionAvailability,
    refreshRBACState,
    syncDisabledPermissions,
    setRBACEnabled,
    token,
    isLogged,
    login,
    ssoGoogleLogin,
    ssoMicrosoftLogin,
    logout,
    mustLogin,
    setMustLogin,
    changePassword,
    recoverPassword,
    getPartner,
    getPartnerInfo,
    partner,
    partnerInfo,
    savePartner,
    getServerVersion,
    setMaintenance,
    maintenance,
    ping,
    enableWebhook,
    getVivaWalletWebhookStatus,
  };
});
